import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import DatePicker from 'react-datepicker'
import Icon from 'components/Icon'
import DatePickerMonthSelector from 'components/Forms/Date/DatePickerMonthSelector/DatePickerMonthSelector'

import './extended-date-range-picker.pcss'

export const ExtendedDateRangePickerContainer = () => {
    // this is where we React.portal the time keeper component
    return <div id="extended-date-range-picker-container" />
}

const ExtendedDateRangePicker = ({
    t,
    placeholderText,
    onDateChange,
    startDate,
    endDate,
    disablePastDates,
    disableFutureDates,
}) => {
    const { i18n } = useTranslation(['formElements'])

    // Function to get weekday name
    const weekdayNames = {
        en: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
        fi: ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La'],
    }

    const getWeekdayName = (dayString) => {
        // Map of weekday names to their indices
        const dayMap = {
            Sunday: 0,
            Monday: 1,
            Tuesday: 2,
            Wednesday: 3,
            Thursday: 4,
            Friday: 5,
            Saturday: 6,
        }

        // Get the day index from the string
        const dayIndex = dayMap[dayString]
        // Return the corresponding weekday name based on the current language
        return weekdayNames[i18n.language]?.[dayIndex]
    }

    const isOutsideRange = useCallback((date) => {
        if (disablePastDates) return moment(date).isBefore(moment(), 'day')
        if (disableFutureDates) return moment(date).isAfter(moment(), 'day')
        return false
    }, [disablePastDates, disableFutureDates])

    const handleDateChange = (date) => {
        const [startDate, endDate] = date
        onDateChange([startDate, endDate])
    }

    const formatDateRange = () => {
        if (startDate && endDate) {
            return `${moment(startDate).format('DD.MM.YYYY')} - ${moment(endDate).format('DD.MM.YYYY')}`
        }
        return ''
    }

    const renderCustomHeader = ({
        date,
        changeMonth,
        changeYear,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
    }) => {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <DatePickerMonthSelector
                    date={moment(date)}
                    month={moment(date)}
                    onMonthSelect={(month, newMonth) => {
                        changeMonth(newMonth)
                    }}
                    onYearSelect={(month, newYear) => {
                        changeYear(newYear)
                    }}
                    isOutsideRange={isOutsideRange}
                    disablePastDates={disablePastDates}
                    disableFutureDates={disableFutureDates}
                    decreaseMonth={decreaseMonth}
                    increaseMonth={increaseMonth}
                    prevMonthButtonDisabled={prevMonthButtonDisabled}
                    nextMonthButtonDisabled={nextMonthButtonDisabled}
                />
            </div>
        )
    }

    return (
        <div>
            <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                showIcon
                toggleCalendarOnIconClick
                icon={(
                    <Icon
                        fa
                        name="calendar-alt"
                        color="#b1b1b1"
                    />
                )}
                renderCustomHeader={renderCustomHeader}
                onChange={handleDateChange}
                dateFormat="dd.MM.yyyy"
                placeholderText={placeholderText || t('formElements:dateRangePicker.placeholder')}
                className="extended-date-range-picker__input"
                monthsShown={1}
                filterDate={date => !isOutsideRange(date)}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                value={formatDateRange()}
                withPortal
                formatWeekDay={(date) => getWeekdayName(date)}
            />
        </div>
    )
}

ExtendedDateRangePicker.propTypes = {
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    onDateChange: PropTypes.func.isRequired,
    disableFutureDates: PropTypes.bool,
    disablePastDates: PropTypes.bool,
    placeholderText: PropTypes.string,
}

export default ExtendedDateRangePicker
